import { TableActionButton } from "_shared/_views/tables/table-toolbar";
import AddBoxIcon from "@mui/icons-material/AddBox";

import { styled, Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_RowData,
  type MRT_TableInstance,
} from "material-react-table";
import {
  searchFieldSpacing,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import useSessionUser from "permissions/session/use-session-user";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

type FeeScheduleInternalActionsToolbarProps<TData extends MRT_RowData> = {
  table: MRT_TableInstance<TData>;
};

export default function FeeScheduleInternalActionsToolbar<TData extends MRT_RowData>(
  props: Readonly<FeeScheduleInternalActionsToolbarProps<TData>>,
) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized element
  // drawn with it never re-renders: the column list kept the switches it first drew (#6599).
  "use no memo";

  const { table } = props;

  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const handleCreateClick = useCallback(() => {
    navigate(`/fee-schedules/${PageTab.Templates}/create`);
  }, [navigate]);

  return (
    <>
      {!isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <TableToolsMenu table={table} />
          </ToolbarButtons>
          {/* Authoring a template asks for read/write on fee schedules. Disabling on identity told a
              read-only member nothing and let them meet the API's refusal at Save (issue #4651). */}
          <TableActionButton
            startIcon={<AddBoxIcon />}
            disabled={!sessionUser?.canCreateFeeScheduleTemplates()}
            onClick={handleCreateClick}
          >
            Create Template
          </TableActionButton>
        </ToolbarButtonsContainer>
      )}
    </>
  );
}
